import BaseComponent from '../base-component/base-component'
import {
  getFocusableTargets,
  _addEventListener,
  _removeEventListener,
  checkTabKey,
} from '../../utils/utils'
import { safePolygon, destroyPolygon } from './safe-polygon'

class FloatElementBtn extends BaseComponent {
  triggerAction: string
  element: any
  polygonRef: any
  safePolygonFn: any
  blurElementTimeoutId: number
  blurLastTargetTimeoutId: number
  mouseenterTimeout: number

  constructor() {
    super()

    this.triggerAction =
      this.dataset.trigger === 'click' ? 'click' : 'mouseenter'

    this.element = this.getElement()

    this.polygonRef = {
      current: null,
    }
  }

  connectedCallback(): void {
    this.element.addEventListener('keyup', this.handleKeyUp)
    this.element.addEventListener('blur', this.handleBlur)

    if (this.dataset.trigger === 'click') {
      window.addEventListener('click', this.handleWindowClick)

      this.element.addEventListener('click', this.handleElementClick)
    } else {
      this.element.addEventListener('mouseleave', this.handleMouseLeave)
      this.element.addEventListener('mouseenter', this.handleMouseEnter)
    }
  }

  disconnectedCallback(): void {
    this.element.removeEventListener('keyup', this.handleKeyUp)
    this.element.removeEventListener('blur', this.handleBlur)

    if (this.dataset.trigger === 'click') {
      window.removeEventListener('click', this.handleWindowClick)

      this.element.removeEventListener('click', this.handleElementClick)
    } else {
      this.element.removeEventListener('mouseleave', this.handleMouseLeave)
      this.element.removeEventListener('mouseenter', this.handleMouseEnter)
    }
  }

  handleBlur = (): void => {
    this.blurElementTimeoutId = setTimeout(() => {
      if (!this.floatElement) {
        return
      }

      // this.floatElement.hide() need refactoring whole component, bad work with click
    }, 0)
  }

  handleKeyUp = (evt: KeyboardEvent): void => {
    const key = evt.key || evt.keyCode

    if (!key && !checkTabKey(key)) return

    this.floatElement.show()
    this.setTargetListeners('add')
  }

  handleFirstTargetFocus = (): void => {
    clearTimeout(this.blurElementTimeoutId)
  }

  handleTargetFocus = (): void => {
    clearTimeout(this.blurLastTargetTimeoutId)
  }

  handleLastTargetBlur = (): void => {
    this.blurLastTargetTimeoutId = setTimeout(() => {
      this.setTargetListeners('remove')

      this.floatElement.hide()
    }, 0)
  }

  handleMouseEnter = (): void => {
    this.mouseenterTimeout = setTimeout(() => {
      this.trigger()
    }, 0)
  }

  handleElementClick = (): void => {
    this.trigger()
  }

  handleWindowClick = (event: MouseEvent): void => {
    const isCursorOutside = this.floatElement.checkCursorOutside(
      event.clientX,
      event.clientX,
    )
    const isOutsideFloatElements = this.isTargetOutsideFloatElements(
      event.target,
    )

    if (isCursorOutside && isOutsideFloatElements) {
      window.removeEventListener('click', this.handleWindowClick)

      this.floatElement.hide()
    }
  }

  handleMouseLeave = (event: MouseEvent): void => {
    clearTimeout(this.mouseenterTimeout)

    if (this.safePolygonFn) {
      window.removeEventListener('mousemove', this.handleMouseMove)
    }

    this.safePolygonFn = safePolygon()({
      x: event.clientX,
      y: event.clientY,
      placement: this.floatElement.dataset.placement,
      onClose: () => {
        window.removeEventListener('mousemove', this.handleMouseMove)

        if (this.floatElement) {
          this.floatElement.hide()
        }

        destroyPolygon(this.polygonRef)
      },
      elements: { domReference: this.element, floating: this.floatElement },
      polygonRef: this.polygonRef,
    })

    this.safePolygonFn(event)

    window.addEventListener('mousemove', this.handleMouseMove)
  }

  handleMouseMove = (event: MouseEvent): void => {
    this.safePolygonFn(event)
  }

  setTargetListeners(action): void {
    const { targets, firstTarget, lastTarget } = this.getTargets()

    const setter = action === 'add' ? _addEventListener : _removeEventListener

    setter(firstTarget, 'focus', this.handleFirstTargetFocus)

    targets.forEach(target => {
      setter(target, 'focus', this.handleTargetFocus)
    })

    setter(lastTarget, 'blur', this.handleLastTargetBlur)
    setter(window, 'click', this.handleWindowClick)
  }

  getElement() {
    const elementSelector = `[data-float-element-trigger="${this.dataset.floatElementId}"]`

    return this.querySelector(elementSelector) || this
  }

  getTargets() {
    const element = this.floatElement.element
    const targets = [...element.querySelectorAll(getFocusableTargets())]
    const firstTarget = targets[0]
    const lastTarget = targets[targets.length - 1]

    return { targets, firstTarget, lastTarget }
  }

  isTargetOutsideFloatElements(target) {
    const isInsideFloatElement = this.floatElement.contains(target)
    const isInsideFloatElementBtn = this.contains(target)

    return !isInsideFloatElement && !isInsideFloatElementBtn
  }

  trigger = (): void => {
    if (this.triggerAction === 'click') {
      this.floatElement.isOpen
        ? this.floatElement.hide()
        : this.floatElement.show()

      window.addEventListener('click', this.handleWindowClick)
    } else {
      this.floatElement.show()

      this.element.addEventListener('mouseleave', this.handleMouseLeave)
    }
  }

  setExpand(isExpanded): void {
    this.setAttribute('data-aria-expanded', isExpanded)
  }

  get floatElement(): any {
    if (!this.dataset.floatElementId) {
      return
    }

    return document.getElementById(this.dataset.floatElementId)
  }

  get isOpen(): boolean {
    return this.floatElement.isOpen
  }
}

export default FloatElementBtn
